import { CSSProperties, ReactElement } from 'react';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import _get from 'lodash/get';

import { RenderProps } from './wf-help';
import { ErrorSvg } from '../wf-svg';

import './index.scss';

interface WfFormItemProps {
  id?: string;
  label?: string;
  name: string;
  defaultValue?: unknown;
  required?: boolean | string;
  width?: string;
  rules?: RegisterOptions;
  t: (key: string) => string;
  render: (props: RenderProps) => ReactElement;
  style?: CSSProperties;
  requiredText?: string;
}

export function WfFormItem(props: WfFormItemProps) {
  const {
    id,
    label,
    name,
    defaultValue,
    required,
    width = '100%',
    rules,
    t,
    render,
    style = {},
    requiredText = `${label} ${t('webform.is_required')}`,
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const errorMsg = _get(errors, name)?.message || '';

  const labelClassName = `wf-form-item-head-label ${errorMsg ? 'wf-form-item-head-label_error' : ''}`;

  return (
    <div className="wf-form-item" id={id} style={{ width, ...style }}>
      {label && (
        <div className="wf-form-item-head">
          <label className={labelClassName} htmlFor={name} title={label}>
            {label}
          </label>

          {required && <span className="wf-form-item-head-required">*</span>}
        </div>
      )}

      <Controller
        name={name}
        defaultValue={defaultValue}
        control={control}
        rules={{
          validate: (value) => {
            if (!required) return true;

            let isValid = false;
            const type = Object.prototype.toString.call(value).slice(8, -1);

            switch (type) {
              case 'String':
                isValid = value.trim() !== '';
                break;
              case 'Boolean':
                isValid = value;
                break;
              case 'Object':
                isValid = Object.keys(value).length > 0;
                break;
              case 'Array':
                isValid = value.length > 0;
                break;
              case 'Date':
                isValid = !isNaN(value.getTime());
                break;
            }

            const requiredMsg = label ? requiredText : '';
            return isValid || (typeof required === 'string' ? required : requiredMsg);
          },
          ...rules,
        }}
        render={(props) => render({ ...props, rules })}
      />

      {errorMsg && (
        <div className="wf-form-item-error">
          <ErrorSvg className="wf-form-item-error-icon" />
          <p className="wf-form-item-error-msg">{errorMsg}</p>
        </div>
      )}
    </div>
  );
}
